import React, { useState } from "react";
import {
  X,
  Copy,
  Check,
  Tag,
  Search,
  Bot,
  Plus,
  Loader,
  AlertCircle,
  Info,
  Download,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Navigation } from "./Navigation";
import { Link } from "react-router-dom";
import ContactModal from "./ContactModal";
import { useNavigate, useLocation } from "react-router-dom";
// Removed EmailJS import - now using Netlify Forms
import BlogTimeline from "./BlogTimeline";
import { usePromptStore } from "../store/promptStore";
import YouTubeEmbed from "./YouTubeEmbed";
import EmailCaptureModal from "./EmailCaptureModal";
import SEO from "./SEO";
import SearchBox from "./SearchBox";
import { SearchService, SearchResult } from "../services/searchService";
import { EnhancedSearchService } from "../services/enhancedSearchService";
import Footer from "./Footer";

// Specialized prompt search function for the Prompt Library
// const promptLibrarySearch = async (query: string): Promise<SearchResult[]> => {
//   try {
//     // Use enhanced search service but only get prompts
//     const searchResponse = await EnhancedSearchService.enhancedSearch(query, {
//       limit: 15, // More results for library context
//       types: ["prompt"], // Only search prompts
//       sortBy: "relevance",
//     });

//     // Return only prompt results with proper navigation
//     return searchResponse.prompts.map(prompt => ({
//       ...prompt,
//       // Ensure proper navigation within the Prompt Library
//       url: `/prompt-library?prompt=${prompt.id}&search=${encodeURIComponent(query)}`
//     }));
//   } catch (error) {
//     console.error("Error searching prompts:", error);
//     // Fallback to basic prompt search
//     return await SearchService.searchPrompts(query, { limit: 15 });
//   }
// };

const learningLevels: LearningLevel[] = [
  {
    level: 1,
    title: "Basic Prompts",
    description: "The Key to Unlocking AI's Full Potential.",
  },
  {
    level: 2,
    title: "Smarter Prompts",
    description: "Better Outcomes, Sharper Insights, Less Frustration .",
  },
  {
    level: 3,
    title: "Advanced Prompts",
    description: "Expert Techniques to Make AI a Real Teammate.",
  },
];

function generateSlug(title: string): string {
  const wordsToRemove = [
    "a",
    "an",
    "the",
    "and",
    "or",
    "but",
    "in",
    "on",
    "at",
    "to",
    "for",
    "of",
    "with",
    "by",
  ];
  return title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .split(/\s+/)
    .filter((word) => !wordsToRemove.includes(word))
    .join("-")
    .replace(/-+/g, "-")
    .replace(/^blog\//, "");
}

function PromptLibrary() {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  const {
    prompts,
    categories,
    tags,
    loading,
    error,
    fetchPrompts,
    fetchCategories,
    fetchTags,
    setSelectedCategory,
    setSelectedTags,
    setSearchQuery,
    selectedCategory,
    selectedTags,
    searchQuery,
  } = usePromptStore();
  const [showSearch, setShowSearch] = useState(false);
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  const [comingSoonLevel, setComingSoonLevel] = useState<number | null>(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  // Fetch data when component mounts
  React.useEffect(() => {
    fetchPrompts();
    fetchCategories();
    fetchTags();
  }, [fetchPrompts, fetchCategories, fetchTags]);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(text);
    setTimeout(() => setCopiedText(null), 2000);
  };

  const handleDownload = (prompt: Prompt) => {
    if (prompt.download_url) {
      setSelectedPrompt(prompt);
      setShowDownloadModal(true);
    }
  };

  const handleLevelClick = (level: number) => {
    if (level === 1) {
      navigate("/blog/level-1-basic-prompts-key-unlocking-ais-full-potential");
    } else if (level === 2) {
      navigate(
        "/blog/level-2-smarter-prompts-better-outcomes-sharper-insights-less-frustration",
      );
    } else {
      setComingSoonLevel(level);
      setShowComingSoonModal(true);
    }
  };

  // Handle search result clicks within the Prompt Library context
  const handleSearchResultClick = (result: SearchResult) => {
    // Extract prompt ID from the URL
    const urlParams = new URLSearchParams(result.url.split("?")[1]);
    const promptId = urlParams.get("prompt");

    if (promptId) {
      // Find the prompt in the current list
      const prompt = prompts.find((p) => p.id === promptId);
      if (prompt) {
        setSelectedPrompt(prompt);
        // Scroll to the prompt details section
        const promptSection = document.getElementById("prompt-details");
        if (promptSection) {
          promptSection.scrollIntoView({ behavior: "smooth" });
        }
      }
    }
  };

  // Enhanced search function that works like navigation search but also filters prompts
  const enhancedPromptLibrarySearch = async (
    query: string,
  ): Promise<SearchResult[]> => {
    try {
      // Use enhanced search service for dropdown results (like navigation search)
      const searchResponse = await EnhancedSearchService.enhancedSearch(query, {
        limit: 15,
        types: ["prompt"],
        sortBy: "relevance",
      });

      // Return enhanced results with proper navigation - extract prompts from the response
      const results = searchResponse.prompts.map((prompt) => ({
        ...prompt,
        url: `/prompt-library?prompt=${prompt.id}&search=${encodeURIComponent(query)}`,
      }));

      return results;
    } catch (error) {
      console.error("❌ Error searching prompts:", error);
      // Fallback to basic prompt search
      const fallbackResults = await SearchService.searchPrompts(query, {
        limit: 15,
      });
      return fallbackResults;
    }
  };

  // Enhanced Schema for the Prompt Library page
  const promptLibrarySchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "AI Prompt Library - One80Labs",
    description:
      "Access our curated collection of AI prompts for ChatGPT, Gemini, and other AI tools. Designed for businesses in Omaha and across the Midwest to improve productivity and workflow automation.",
    url: "https://one80labs.com/prompt-library",
    isPartOf: {
      "@type": "WebSite",
      name: "One80Labs",
      url: "https://one80labs.com",
    },
    about: {
      "@type": "Thing",
      name: "AI Prompts",
      description:
        "Structured prompts for AI tools like ChatGPT and Gemini to enhance productivity and workflow automation",
    },
    provider: {
      "@type": "Organization",
      name: "One80Labs",
      url: "https://one80labs.com",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Omaha",
        addressRegion: "NE",
        postalCode: "68181",
        addressCountry: "US",
      },
    },
    mainEntity: {
      "@type": "ItemList",
      itemListElement: prompts.slice(0, 10).map((prompt, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "CreativeWork",
          name: prompt.title,
          description: prompt.description,
          url: `https://one80labs.com/prompt-library?prompt=${prompt.id}`,
          author: {
            "@type": "Organization",
            name: "One80Labs",
          },
          datePublished: prompt.published_at || prompt.created_at,
          dateModified: prompt.updated_at,
          isAccessibleForFree: "True",
          genre: "AI Prompt",
          keywords:
            (prompt as any).prompt_tags_map
              ?.map((tag: any) => tag.prompt_tags.name)
              .join(", ") || "",
        },
      })),
    },
    // Add educational content schema
    educationalLevel: "Beginner to Advanced",
    educationalUse: "Professional Development",
    learningResourceType: "Prompt Library",
    teaches: [
      "AI Prompt Engineering",
      "ChatGPT Optimization",
      "Gemini Usage",
      "Business Automation",
      "Content Creation",
      "Data Analysis",
    ],
    // Add service schema for the prompt library service
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "AI Prompt Library Services",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Free AI Prompt Library",
            description:
              "Access to curated AI prompts for business productivity",
          },
          price: "0",
          priceCurrency: "USD",
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "AI Prompt Engineering Training",
            description:
              "Professional training in prompt engineering for Midwest businesses",
          },
        },
      ],
    },
  };

  // Local business schema for better local SEO
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "One80Labs - AI Prompt Engineering in Omaha",
    image: "https://one80labs.com/logo.png",
    description:
      "The Midwest's Premier AI Agency providing prompt engineering expertise and AI training in Omaha, Nebraska and surrounding areas.",
    "@id": "https://one80labs.com",
    url: "https://one80labs.com/prompt-library",
    telephone: "+1-402-915-3613",
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      streetAddress: "4383 Nicholas Street #302",
      addressLocality: "Omaha",
      addressRegion: "NE",
      postalCode: "68181",
      addressCountry: "US",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 41.2651,
      longitude: -95.9397,
    },
    areaServed: [
      {
        "@type": "City",
        name: "Omaha",
        sameAs: "https://en.wikipedia.org/wiki/Omaha,_Nebraska",
      },
      {
        "@type": "City",
        name: "Lincoln",
        sameAs: "https://en.wikipedia.org/wiki/Lincoln,_Nebraska",
      },
      {
        "@type": "City",
        name: "Council Bluffs",
        sameAs: "https://en.wikipedia.org/wiki/Council_Bluffs,_Iowa",
      },
      {
        "@type": "State",
        name: "Nebraska",
        sameAs: "https://en.wikipedia.org/wiki/Nebraska",
      },
    ],
  };

  // FAQ schema for voice search optimization
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is a prompt library?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "A prompt library is a collection of pre-written, structured prompts designed to help you get better results from AI tools like ChatGPT and Gemini. Our prompt library includes templates for content creation, business analysis, coding assistance, and more.",
        },
      },
      {
        "@type": "Question",
        name: "How can businesses in Omaha use AI prompts?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Businesses in Omaha and throughout Nebraska can use AI prompts to automate routine tasks, generate content, analyze data, improve customer service, and streamline operations. Our prompt library is designed to help Midwest businesses implement AI effectively without technical expertise.",
        },
      },
      {
        "@type": "Question",
        name: "Do I need coding experience to use these prompts?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "No coding experience is required. Our prompts are designed to be copy-pasted directly into AI tools like ChatGPT, Gemini, or Microsoft 365 Copilot. They're ready to use for professionals at any technical level.",
        },
      },
      {
        "@type": "Question",
        name: "How do I learn more about prompt engineering?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "You can start with our Level 1 Basic Prompts guide, which teaches fundamental prompt engineering techniques. For more advanced training, One80Labs offers workshops and courses specifically designed for businesses in Omaha and the Midwest region.",
        },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="AI Prompt Library for Omaha Businesses | One80Labs"
        description="Access our free library of AI prompts for ChatGPT, Gemini, and Microsoft 365 Copilot. Designed for businesses in Omaha and across the Midwest to improve productivity and automate workflows."
        image="https://images.unsplash.com/photo-1677442135968-6d89485dd1d1"
        jsonLd={[
          promptLibrarySchema as any,
          localBusinessSchema as any,
          faqSchema as any,
        ]}
        keywords={[
          "AI prompts Omaha",
          "ChatGPT prompts Nebraska",
          "Gemini prompts Midwest",
          "prompt engineering Omaha",
          "AI templates Nebraska",
          "business AI prompts Omaha",
          "productivity prompts Midwest",
          "content creation prompts Nebraska",
          "AI workflow automation Omaha",
          "prompt library Nebraska",
          "AI prompt examples Omaha",
          "business automation prompts Midwest",
          "AI prompt templates Nebraska",
          "prompt engineering guide Omaha",
          "local AI resources Nebraska",
          "Midwest AI prompts",
          "Omaha technology resources",
          "Nebraska AI tools",
          "voice search optimization Omaha",
          "AI for small business Nebraska",
        ]}
      />
      <Navigation onSearchClick={() => setShowSearch(!showSearch)} />
      {showSearch && (
        <div className="max-w-3xl mx-auto px-4 py-3">
          <SearchBox />
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Learn AI Prompt Engineering Section */}
        <div className="mb-12 bg-gray-50 p-8 rounded-lg">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Learn AI Prompt Engineering
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {learningLevels.map((level) => (
              <div
                key={level.level}
                className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col h-full"
              >
                <div className="flex-grow">
                  <div className="text-sm font-medium text-[#F98B3D] mb-2">
                    Level {level.level}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {level.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{level.description}</p>
                </div>
                <button
                  onClick={() => handleLevelClick(level.level)}
                  className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F98B3D]"
                >
                  Start Learning
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 ml-2"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Prompt Library Heading */}
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          Prompt Library
        </h2>

        {/* Enhanced Search Bar */}
        <div className="mb-8" aria-label="Search prompts">
          <div className="max-w-md mx-auto">
            <SearchBox
              onSearch={enhancedPromptLibrarySearch}
              onResultClick={handleSearchResultClick}
              placeholder="Search prompts by title, description, or tags..."
              className="w-full"
              showAdvancedFeatures={true}
            />
          </div>
        </div>

        {/* Category Navigation */}
        <div
          className="mb-8 border-b border-gray-200 overflow-x-auto"
          role="navigation"
          aria-label="Prompt categories"
        >
          <nav className="flex whitespace-nowrap">
            <button
              onClick={() => setSelectedCategory("all")}
              aria-pressed={selectedCategory === "all"}
              className={`py-2 px-6 text-sm font-medium border-b-2 ${
                selectedCategory === "all"
                  ? "border-[#F98B3D] text-[#F98B3D]"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              } transition-colors duration-200`}
            >
              All Prompts
            </button>
            {/* Featured category first after All Prompts */}
            {categories
              .filter((category) => category.name === "Featured")
              .map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  aria-pressed={selectedCategory === category.id}
                  className={`py-2 px-6 text-sm font-medium border-b-2 ${
                    selectedCategory === category.id
                      ? "border-[#F98B3D] text-[#F98B3D]"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  } transition-colors duration-200`}
                >
                  {category.name}
                </button>
              ))}

            {/* All other categories */}
            {categories
              .filter((category) => category.name !== "Featured")
              .map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  aria-pressed={selectedCategory === category.id}
                  className={`py-2 px-6 text-sm font-medium border-b-2 ${
                    selectedCategory === category.id
                      ? "border-[#F98B3D] text-[#F98B3D]"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  } transition-colors duration-200`}
                >
                  {category.name}
                </button>
              ))}
          </nav>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Loader className="h-8 w-8 text-[#F98B3D] animate-spin" />
          </div>
        ) : error ? (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md mb-6">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {prompts.map((prompt) => (
                <article
                  key={prompt.id}
                  className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden flex flex-col"
                  onClick={() => {
                    // Create URL-friendly slug from title using the same function as PromptDetail
                    const slug = prompt.title
                      ? generateSlug(prompt.title)
                      : prompt.id;
                    navigate(`/prompt/${slug}`);
                  }}
                  aria-label={`Prompt: ${prompt.title}`}
                >
                  <div className="p-6 flex-grow">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {prompt.title}
                    </h3>
                    {prompt.published_at && (
                      <p className="text-xs text-gray-500 mb-2">
                        Published:{" "}
                        {new Date(prompt.published_at).toLocaleDateString()}
                      </p>
                    )}
                    <p className="text-gray-600 mb-4">{prompt.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {(prompt as any).prompt_tags_map?.map((tagMap: any) => (
                        <span
                          key={tagMap.prompt_tags.id}
                          className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                        >
                          {tagMap.prompt_tags.name}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex justify-end">
                    <button className="text-sm font-medium text-gray-900">
                      View Details
                    </button>
                  </div>
                </article>
              ))}
            </div>

            {prompts.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-600">
                  No prompts found matching your criteria.
                </p>
                {(searchQuery ||
                  selectedCategory !== "all" ||
                  selectedTags.length > 0) && (
                  <button
                    onClick={() => {
                      setSearchQuery("");
                      setSelectedCategory("all");
                      setSelectedTags([]);
                    }}
                    className="mt-4 text-[#F98B3D] hover:text-[#e07a2c]"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </main>

      {/* Contact CTA Section */}
      <div className="bg-gray-50 py-12 border-t border-gray-200">
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm p-8 border border-gray-200">
            <div className="text-center max-w-3xl mx-auto">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Need Help With AI Prompts?
              </h3>
              <div className="flex justify-center mb-4">
                <button
                  onClick={() => navigate("/prompt-admin/login")}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Admin Login
                </button>
              </div>
              <p className="text-gray-600 mb-6">
                Our team of AI experts can help you craft custom prompts for
                your specific business needs. Whether you're looking to automate
                tasks, improve content creation, or enhance customer
                interactions, we're here to help.
              </p>
              <button
                onClick={() => setIsContactModalOpen(true)}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-[#F98B3D] hover:bg-[#e07a2c] transition-colors duration-200"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
                Contact Our Prompt Engineers
              </button>
            </div>
          </div>
        </section>
      </div>

      {/* Latest Updates Timeline Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            Blog Timeline
          </h2>
          <BlogTimeline />
        </div>
      </div>

      <Footer />

      {/* Prompt Modal */}
      {selectedPrompt && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="prompt-title"
          onClick={() => setSelectedPrompt(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-200 bg-gray-50">
              <div className="flex justify-between items-start">
                <div>
                  <h2
                    id="prompt-title"
                    className="text-2xl font-bold text-gray-900"
                  >
                    {selectedPrompt.title}
                  </h2>
                  <p className="text-gray-600 mt-1">
                    {selectedPrompt.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {(selectedPrompt as any).prompt_tags_map?.map(
                      (tagMap: any) => (
                        <span
                          key={tagMap.prompt_tags.id}
                          className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                        >
                          {tagMap.prompt_tags.name}
                        </span>
                      ),
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setSelectedPrompt(null)}
                  className="text-gray-400 hover:text-gray-500 transition-colors"
                  aria-label="Close prompt details"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="prose max-w-none">
                {/* YouTube Video Embed */}
                {selectedPrompt.youtube_video_id && (
                  <div className="mb-6 aspect-video w-full rounded-lg overflow-hidden">
                    <iframe
                      width="100%"
                      height="100%"
                      src={`https://www.youtube.com/embed/${selectedPrompt.youtube_video_id}`}
                      title="YouTube video"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                )}

                {/* Extract and display the overview from the content */}
                {selectedPrompt.content.includes("## Overview") && (
                  <div className="mb-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Overview
                    </h3>
                    <p className="text-gray-700">
                      {selectedPrompt.content
                        .split("## Overview")[1]
                        .split("##")[0]
                        .trim()}
                    </p>
                  </div>
                )}
                <ReactMarkdown>{selectedPrompt.content}</ReactMarkdown>
              </div>

              {selectedPrompt.youtube_video_id && (
                <div className="mt-6 mb-6">
                  <YouTubeEmbed
                    videoId={selectedPrompt.youtube_video_id}
                    title={selectedPrompt.title}
                  />
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end">
              <div className="flex space-x-3">
                {selectedPrompt.download_url && (
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleDownload(selectedPrompt);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F98B3D]"
                  >
                    <Download className="h-5 w-5 mr-2" />
                    {selectedPrompt.download_button_text || "Download Resource"}
                  </a>
                )}
                <button
                  onClick={() => handleCopy(selectedPrompt.content)}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F98B3D]"
                  aria-label={
                    copiedText === selectedPrompt.content
                      ? "Copied to clipboard"
                      : "Copy prompt to clipboard"
                  }
                >
                  {copiedText === selectedPrompt.content ? (
                    <>
                      <Check className="h-5 w-5 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-5 w-5 mr-2" />
                      Copy Prompt
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Coming Soon Modal */}
      {showComingSoonModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="coming-soon-title"
          onClick={() => setShowComingSoonModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Info className="h-6 w-6 text-[#F98B3D] mr-2" />
                <h3
                  id="coming-soon-title"
                  className="text-xl font-semibold text-gray-900"
                >
                  Coming Soon
                </h3>
              </div>
              <button
                onClick={() => setShowComingSoonModal(false)}
                className="text-gray-400 hover:text-gray-500 transition-colors"
                aria-label="Close dialog"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <p className="text-gray-600 mb-6">
              Level {comingSoonLevel} content is currently in development and
              will be available soon. Check back later for more advanced prompt
              engineering techniques!
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setShowComingSoonModal(false)}
                className="px-4 py-2 bg-[#F98B3D] text-white rounded-md hover:bg-[#e07a2c] transition-colors"
                aria-label="Close dialog"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Modal */}
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        // Using Netlify Forms - no onSubmit prop needed
      />

      {/* Email Capture Modal for Downloads */}
      {selectedPrompt && showDownloadModal && (
        <EmailCaptureModal
          isOpen={showDownloadModal}
          onClose={() => setShowDownloadModal(false)}
          downloadUrl={selectedPrompt.download_url || ""}
          promptId={selectedPrompt.id}
          source={`Prompt Library Download: ${selectedPrompt.title}`}
          title="Download Resource"
          description="Please enter your name and email to access this resource. We respect your privacy and will never share your information."
        />
      )}
    </div>
  );
}

// Types
interface LearningLevel {
  level: number;
  title: string;
  description: string;
}

// Local Prompt interface that matches the actual data structure from Supabase
interface Prompt {
  id: string;
  title: string;
  description: string;
  content: string;
  category_id?: string;
  prompt_categories?: {
    id: string;
    name: string;
    slug: string;
  };
  prompt_tags_map?: Array<{
    prompt_tags: {
      id: string;
      name: string;
      slug: string;
    };
  }>;
  is_featured: boolean;
  is_published: boolean;
  created_at: string;
  updated_at: string;
  deleted: boolean;
  published_at?: string | null;
  download_url?: string;
  download_button_text?: string;
  youtube_video_id?: string;
}

export default PromptLibrary;
